// RenamePlaylistContent.tsx
import type React from "react";
import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";

import {usePlaylist} from "~/api/hooks/usePlaylist";
import {useUpdatePlaylist} from "~/api/hooks/useUpdatePlaylist";
import CustomButton from "~/component/common/CustomButton";

import ButtonSpinner from "../../../../component/spinner/ButtonSpinner";

interface RenamePlaylistContentProps {
  closeModal: () => void;
}

const RenamePlaylistContent: React.FC<RenamePlaylistContentProps> = ({
  closeModal,
}) => {
  const {playlistId} = useParams<{playlistId: string}>();
  const {data: playlist} = usePlaylist({id: playlistId});
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [newPlaylistDescription, setNewPlaylistDescription] = useState("");

  const {
    mutateAsync: updatePlaylist,
    isPending: isUpdating,
    isSuccess,
    reset,
  } = useUpdatePlaylist();

  // Initialize the input fields with the current playlist name and description
  useEffect(() => {
    if (playlist?.name) {
      setNewPlaylistName(playlist.name);
    }
    if (playlist?.description) {
      setNewPlaylistDescription(playlist.description);
    }
  }, [playlist]);

  // Close the modal when update is successful
  useEffect(() => {
    if (isSuccess) {
      closeModal();
      reset();
    }
  }, [isSuccess, closeModal, reset]);

  const handleUpdate = async () => {
    if (!playlist || !newPlaylistName.trim()) return;

    try {
      await updatePlaylist({
        data: {
          id: playlist.id,
          name: newPlaylistName.trim(),
          description: newPlaylistDescription.trim(),
        },
      });
    } catch (error) {
      console.error("Error updating playlist:", error);
    }
  };

  return (
    <div className="w-full text-white">
      <h2 className="text-2xl mb-4">Update Playlist</h2>
      <input
        type="text"
        value={newPlaylistName}
        onChange={(e) => setNewPlaylistName(e.target.value)}
        placeholder="Playlist Name"
        className="w-full mb-4 p-2 border rounded bg-white text-black"
      />
      <input
        type="text"
        value={newPlaylistDescription}
        onChange={(e) => setNewPlaylistDescription(e.target.value)}
        placeholder="Playlist Description"
        className="w-full mb-4 p-2 border rounded bg-white text-black"
      />
      <div className="flex justify-end mt-4">
        <CustomButton
          className={`bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ${
            !newPlaylistName.trim() ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={handleUpdate}
          disabled={!newPlaylistName.trim() || isUpdating}
        >
          <ButtonSpinner isLoading={isUpdating} />
          Update
        </CustomButton>
      </div>
    </div>
  );
};

export default RenamePlaylistContent;
